import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  extractLotProperties,
  implementsMoleculeLotInterface,
} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import type {StoichiometryStructuredTableRowMetadata} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  // eslint-disable-next-line camelcase
  GetLotPropertiesQuery_bioentities_SQY4U0ubyEWHLOn5sduVfURNlnZZXXFocgAxXqHC3Q as LotBioentityNode,
  GetLotPropertiesQuery_Query as LotPropertiesQueryData,
  GetLotPropertiesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetLotPropertiesQuery';
import {GET_LOT_PROPERTIES_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {LiveCellValue} from '../LiveCellValues';
import {
  StructuredTableRowUpdater,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from '../StructuredTableConstants';

/**
 * Build a FLOAT-typed live value that matches the shape the stoichiometry evaluator produces for
 * numeric columns (purity, concentration). Using FLOAT (rather than STRING) is required so that
 * areTypedValuesEquivalent compares like-for-like against the evaluated cell value and the
 * server-stored lastSyncedUpstreamValue: a STRING "12" coerces to "12" while a FLOAT 12 coerces to
 * 12, and lodash.isEqual treats those as unequal, producing a permanent false-positive "outdated"
 * indicator on these columns.
 */
function makeFloatLiveValue(value: string): LiveCellValue {
  const preciseDecimal = PreciseDecimal.ofOrUndefined(value);
  if (preciseDecimal == null) {
    // Value came from the lot's own schema tag, so it should always be parsable; but the value
    // may fall back to a raw un-parsable string when extractLotProperties can't canonicalize it
    // (see extractPreciseDecimalValue). Rather than throw and break the row updater, treat it as
    // "no live value" so a malformed lot tag can't produce a false stale/outdated indicator.
    return 'NO_VALUE';
  }
  return {
    type: FieldType.FLOAT,
    value,
    rawValue: value,
    floatValue: preciseDecimal.toFloat(),
  };
}

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<LotBioentityNode | InaccessibleInfoGraphQL>> {
  const data = await doQuerySafe<LotPropertiesQueryData, GetLotPropertiesQueryVariables>(
    {query: GET_LOT_PROPERTIES_QUERY},
    {
      variables: {
        lotIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  // Cast is safe: InaccessibleInfoNode satisfies InaccessibleInfo structurally
  return data.bioentities as Array<LotBioentityNode | InaccessibleInfoGraphQL>;
}

export const StoichiometryLotRowUpdater: StructuredTableRowUpdater<
  LotBioentityNode,
  StoichiometryStructuredTableRowMetadata
> = {
  /**
   * updateRow is required by the StructuredTableRowUpdater interface, but this updater is
   * liveDataOnly (see below), so updateRow is never actually invoked in practice: liveDataOnly
   * updaters are filtered out before any refresh flow gets to the point of calling updateRow.
   * This implementation exists only to satisfy the type and returns the row unchanged.
   */
  updateRow(_model, _columns, row, _columnGroup) {
    return row;
  },

  /**
   * Purity/concentration/molecule cell values are computed by the stoichiometry custom evals;
   * their LSUV baseline is only set when the reaction group is submitted (via the reaction group
   * submit action function), not by table refresh. liveDataOnly tells refreshRowsWithRowUpdater to
   * skip this updater (and its LSUV recompute) entirely, so refreshing the table can't clobber
   * that baseline.
   */
  liveDataOnly: true,

  fetchModel(sampleLookupInfo: SampleLookupInfo): Promise<LotBioentityNode | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<LotBioentityNode | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache({idForLookup}: SampleLookupInfo): LotBioentityNode | null {
    const data = readQuerySafe<LotPropertiesQueryData, GetLotPropertiesQueryVariables>(
      {query: GET_LOT_PROPERTIES_QUERY},
      {variables: {lotIds: [idForLookup]}}
    );
    const node = data?.bioentities?.[0];
    if (node == null || isInaccessible(node)) {
      return null;
    }
    if (!implementsMoleculeLotInterface(node)) {
      return null;
    }
    return node;
  },

  updateCell(
    model: LotBioentityNode | InaccessibleInfoGraphQL,
    _columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }
    if (!implementsMoleculeLotInterface(model)) {
      return 'NO_VALUE';
    }

    const properties = extractLotProperties(model.tagsJson);

    switch (column.columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY:
        return properties.purity != null ? makeFloatLiveValue(properties.purity) : 'NO_VALUE';

      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION:
        if (properties.concentrationMolar != null) {
          return makeFloatLiveValue(properties.concentrationMolar);
        }
        if (properties.concentrationDensity != null) {
          return makeFloatLiveValue(properties.concentrationDensity);
        }
        return 'NO_VALUE';

      // TODO(BNCH-162721): Concentration unit staleness detection deferred until follow-up PR
      // resolves the STRING vs UNIT_REFERENCE type mismatch.
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT:
        return 'NO_VALUE';

      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE:
        return properties.parentMoleculeId != null
          ? {
              type: BaseTypedValueType.BIOENTITY,
              bioentitySummary: {
                id: properties.parentMoleculeId,
                name: properties.parentMoleculeName ?? '',
              },
            }
          : 'NO_VALUE';

      default:
        return 'NO_VALUE';
    }
  },
};
